import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { fromEvent, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationService } from '@ieCore/services/organization.service';
import { AuthService } from '@ieCore/services/auth.service';
import { NotificationService } from '@ieCore/services/notification.service';
import { NavService } from '@ieCore/services/nav.service';
import { NO_TITLE } from '@ieCore/consts/notification.consts';
import * as UserActions from '@ieApp/store/user/user.actions';

/** How long before expiry the warning modal appears. */
const WARNING_LEAD_MS = 60_000;

/** Throttle window for the merged user-input stream. */
const INPUT_THROTTLE_MS = 1_000;

/**
 * Item 11: user-input events that count as "activity". Deliberately DOES
 * NOT include any HTTP/network signal — report-details polls every 5s and
 * would otherwise make an idle session look perpetually active.
 */
const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
];

/**
 * Item 11: idle-input-driven logout. Tracks real user input (never HTTP
 * traffic) against a resettable timer sized to the CURRENT tenant's
 * `Org.idleLogoutMinutes`; shows a "Stay signed in" warning at T-60s, and
 * on expiry calls the existing revoking logout path
 * (AuthService.logout -> POST /auth/logout, which invalidates the refresh
 * token server-side and navigates to login).
 *
 * INERT by construction when the tenant has no configured minutes (absent,
 * `0`, or an org-fetch failure — OrganizationService.getIdleLogoutMinutes()
 * already fails open to `0`): start() subscribes, sees `0`, and never
 * starts a timer or attaches input listeners.
 *
 * Shares OrganizationService's resolve-once cached org fetch with Item 6's
 * form-defaults reader — this service costs zero EXTRA org HTTP calls.
 */
@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  private started = false;
  private configuredMinutes = 0;
  private orgMinutesSub?: Subscription;
  private inputSub?: Subscription;
  private warningTimeoutId?: ReturnType<typeof setTimeout>;
  private expiryTimeoutId?: ReturnType<typeof setTimeout>;
  private modalRef?: NzModalRef;

  constructor(
    private organizationService: OrganizationService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private modalService: NzModalService,
    private translateService: TranslateService,
    private store: Store,
    private router: Router,
    private navService: NavService,
    private ngZone: NgZone,
  ) {}

  /**
   * Starts idle-input tracking for the current session. Safe to call
   * multiple times (e.g. on every login) — a second call while already
   * started is a no-op. Wire once, app-level, gated on authenticated state.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    // Stored (not fire-and-forget): a logout can race the still-pending
    // org fetch (getIdleLogoutMinutes() is now LIVE per the org-config
    // fix and never completes on its own) — without unsubscribing here,
    // stop() couldn't cancel it, and a late response would re-arm the
    // tracker AFTER logout, leaking into the next login's session.
    this.orgMinutesSub = this.organizationService
      .getIdleLogoutMinutes()
      .subscribe((minutes) => {
        if (minutes <= 0) {
          return; // INERT: no tenant flag configured for this org
        }
        this.configuredMinutes = minutes;
        this.trackInput();
        this.resetTimer();
      });
  }

  /** Tears down the org-minutes subscription, all timers/listeners/modal. Call on logout. */
  stop(): void {
    this.orgMinutesSub?.unsubscribe();
    this.orgMinutesSub = undefined;
    this.inputSub?.unsubscribe();
    this.inputSub = undefined;
    this.clearPending();
    this.started = false;
    this.configuredMinutes = 0;
  }

  private trackInput(): void {
    // Raw mousemove/scroll/etc fire far too often to run each one through
    // Angular's zone (a change-detection pass per event) — attach the
    // listeners OUTSIDE the zone. resetTimer() itself only does timer
    // bookkeeping (no UI change), and the two spots that DO touch
    // UI-visible state (showWarning, expireSession) explicitly re-enter
    // the zone themselves.
    this.ngZone.runOutsideAngular(() => {
      this.inputSub = merge(
        ...ACTIVITY_EVENTS.map((eventName) => fromEvent(document, eventName)),
      )
        .pipe(throttleTime(INPUT_THROTTLE_MS))
        .subscribe(() => {
          // Once the warning modal is up, passive activity (a stray
          // mousemove) must not silently dismiss it — only the explicit
          // Stay-signed-in button (nzOnOk -> resetTimer) or expiry may.
          if (this.modalRef) {
            return;
          }
          this.resetTimer();
        });
    });
  }

  private resetTimer(): void {
    if (!this.configuredMinutes) {
      return;
    }
    this.clearPending();

    const totalMs = this.configuredMinutes * 60_000;
    const warningLeadMs = Math.min(WARNING_LEAD_MS, totalMs);
    this.warningTimeoutId = setTimeout(
      () => this.showWarning(warningLeadMs),
      totalMs - warningLeadMs,
    );
  }

  /** Cancels the pending warning/expiry timers and dismisses the warning modal. */
  private clearPending(): void {
    clearTimeout(this.warningTimeoutId);
    clearTimeout(this.expiryTimeoutId);
    this.warningTimeoutId = undefined;
    this.expiryTimeoutId = undefined;
    this.modalRef?.close();
    this.modalRef = undefined;
  }

  /**
   * Re-enters the Angular zone here: resetTimer() (and the setTimeout it
   * schedules) may have run outside it via trackInput's DOM-event handler,
   * but creating the warning modal is a UI change and needs Angular's zone
   * to render/track it correctly.
   */
  private showWarning(warningLeadMs: number): void {
    this.ngZone.run(() => {
      this.modalRef = this.modalService.warning({
        nzTitle: this.translateService.instant(
          'LOGIN.INACTIVITY_WARNING_TITLE',
        ),
        nzContent: this.translateService.instant(
          'LOGIN.INACTIVITY_WARNING_MESSAGE',
        ),
        nzOkText: this.translateService.instant('LOGIN.STAY_SIGNED_IN'),
        nzClosable: false,
        nzMaskClosable: false,
        nzOnOk: () => this.resetTimer(),
      });

      this.expiryTimeoutId = setTimeout(
        () => this.expireSession(),
        warningLeadMs,
      );
    });
  }

  /** Re-enters the Angular zone for the same reason as showWarning() above. */
  private expireSession(): void {
    this.ngZone.run(() => {
      const refreshToken = this.authService.getAuthData()?.refreshToken;
      this.stop();

      this.authService.logout({ refreshToken }).response.subscribe({
        next: () =>
          this.notificationService.info(
            NO_TITLE,
            'LOGIN.INACTIVITY_LOGOUT_MESSAGE',
          ),
        // AuthService.logout only clears local auth + navigates to login
        // INSIDE its success tap, so a failed revoke (5xx / offline) would
        // otherwise leave the user silently signed in — with the tracker
        // above already torn down by stop(), idle logout would then be dead
        // for the rest of the session. Idle logout is a UX security
        // measure, not a network operation: a failed server-side revoke
        // must still end the LOCAL session. The orphaned refresh token
        // simply dies at its natural TTL instead of being revoked
        // immediately.
        error: () => this.forceLocalSignOut(),
      });
    });
  }

  /**
   * Mirrors AuthService.logout's success-path local effects (clear stored
   * auth, clear the user store, route to login) for the revoke-failed case
   * — see the comment on the `error` branch above for why this is needed.
   * Deliberately does NOT touch AuthService.logout itself.
   */
  private forceLocalSignOut(): void {
    this.authService.removeAuthData();
    this.store.dispatch(UserActions.clear());
    this.router.navigate(this.navService.to.login()).then();
    this.notificationService.info(NO_TITLE, 'LOGIN.INACTIVITY_LOGOUT_MESSAGE');
  }
}
